import * as React from "react";
import { Swiper, SwiperSlide } from 'swiper/react';
import SEO from "../components/seo";
import Layout from "../components/layout";
import SimpleHero from "../components/v2/simple-hero";
import FiftyFiftyHorizontal from "../components/v2/fifty-fifty-horizontal";
import ImageGridBanner from "../components/v2/image-grid-banner";
import ImageComparison from "../components/v2/image-comparison";
import HeadlineBanner from "../components/v2/headline-banner";
import MediaSlider from "../components/v2/media-slider";
import GetUpdates from "../components/v2/get-updates";
import DetailsFooter from "../components/v2/details-footer";
import Quote from "../components/v2/quote";
import Spec from "../components/v2/spec";
import ArrowLink from "../components/global/arrow-link";

// Logo
import BPLogo from "../assets/bp-logo.svg";
import AtsLogo from "../assets/ats-logo.svg";
import L9Logo from "../assets/l9-logo.svg";
import EvoHotelLogo from "../assets/evo-hotel-logo-2-black.svg";

// Hooks
import useTheVisionPageImages from "../hooks/useTheVisionPageImages";
import useCommonPageImages from "../hooks/useCommonPageImages";
import YoutubeVideo from "../components/v2/youtube-video";

const TheVision = () => {
  return (
    <Layout
      className="the-vision"
      bodyTheme="light"
      isFooter={false}
      headerLayout="mobile"
    >
      <SEO title="The Vision" />
      <SimpleHero
        contentData={
          {
            pre_title: "The",
            title: "Vision",
          }
        }
        imageData={useTheVisionPageImages({name: "the-vision-1"})}
      />
      <section className="spacing spacing--general">
        <HeadlineBanner
          pre_title="The next step in"
          title="Our evolution"
        />
        <section className="spacing spacing--spacing-top-1">
          <div className="container">
            <div className="row">
              <div className="col-12 col-lg-8">
                <Quote
                  quote="At Campus Salt Lake, we have an amazing mix of complementary businesses, concepts and people all focused around community and bringing people together to share their passion for the outdoors.”"
                  author="Bryce Phillips,"
                  position="Founder and CEO of evo"
                />
              </div>
            </div>
          </div>
        </section>
      </section>
      <YoutubeVideo
        className="spacing spacing--general"
        youtubeId="OBpK4hi6m8s"
      />
      <FiftyFiftyHorizontal
        mediaPosition="left"
        contentColumns={5}
        contentOffset={1}
        mediaColumns={6}
        mediaOffset={0}
        imageData={useTheVisionPageImages({name: "the-vision-2"})}
      >
        <p>Since evo’s founding in 2001, our purpose has been to make life better by building community and igniting the adventurous spirit that lives within us all.</p>
        <p>This purpose has guided us as we’ve grown to provide our community with more ways to connect with what they love - the outdoors, recreation, art, culture and beyond.</p>
      </FiftyFiftyHorizontal>
      <ImageGridBanner
        className="spacing spacing--spacing-3"
        imageData={useTheVisionPageImages({name: "the-vision-3"})}
      />
      <FiftyFiftyHorizontal
        contentColumns={5}
        contentOffset={1}
        mediaColumns={5}
        mediaOffset={1}
        imageData={useTheVisionPageImages({name: "the-vision-4"})}
      >
        <p>We started out in a garage, and over the years went on to build stores in Seattle, Portland, Denver, and Whistler, BC - with more locations on the way soon. But since the beginning, we’ve set out to be much more than a retailer. And our growth has been a story of adding layers; of building richer experiences. We see Campus Salt Lake as the culmination of so much of what we’ve been working toward and dreaming about for the past 20 years. We invite all humans to experience it with us.</p>
      </FiftyFiftyHorizontal>
      <ImageComparison
        className="spacing spacing--general"
        itemOneImageData={useCommonPageImages({name: "comparison-before-1"})}
        itemTwoImageData={useCommonPageImages({name: "comparison-after-1"})}
      />
      <HeadlineBanner
        pre_title="A piece of history"
        title="Re-imagined"
      />
      <FiftyFiftyHorizontal
        className="spacing spacing--general"
        imageData={useTheVisionPageImages({name: "the-vision-5"})}
      >
        <p>We’re transforming 100,000 square feet of warehouse space in Salt Lake City’s Granary District. Campus is an assembly of five 100 year-old previously disjointed warehouses. The next era will stitch together these spaces into a new kind of gathering place for our community. One section of the building has been designated as a National Historic Landmark.</p>
      </FiftyFiftyHorizontal>
      <ImageComparison
        generalImage={useTheVisionPageImages({name: "the-vision-6"})}
      />
      <HeadlineBanner
        className="spacing spacing--general"
        pre_title="Meet The"
        title="Granary District"
      />
      <FiftyFiftyHorizontal
        mediaPosition="left"
        contentColumns={5}
        contentOffset={1}
        mediaColumns={6}
        mediaOffset={0}
        imageData={useTheVisionPageImages({name: "the-vision-7"})}
      >
        <p>The Granary District was once an industrial area built up around Salt Lake’s railroad corridor. When the rail lines moved west, the Granary District underwent a period of urban decay. Recently, the neighborhood has been rediscovered and it’s coming back to life in a big way. It’s become a haven for artists and makers - with new breweries, restaurants and shops opening up everywhere. We’re excited to be a part of the renaissance that’s happening in the Granary District - this place has the kind of energy we love.</p>
      </FiftyFiftyHorizontal>
      <MediaSlider
        className="spacing spacing--general"
        slides={[
          {
            imageData: useTheVisionPageImages({name: "the-vision-8"}),
            description: "",
          },
          {
            imageData: useTheVisionPageImages({name: "the-vision-9"}),
            description: "",
          },
        ]}
      />

      <FiftyFiftyHorizontal
        className="spacing spacing--general"
        contentColumns={5}
        mediaColumns={5}
        mediaOffset={2}
        imageData={useTheVisionPageImages({name: "the-vision-15"})}
      >
        <div className="spec-container spec-container--mobile spec-container--the-vision">
          <Swiper
            slidesPerView={'auto'}
            loop={true}
            spaceBetween={60}
            breakpoints={{
              768: {
                spaceBetween: 40,
              }
            }}
          >
            <SwiperSlide>
              <Spec
                subTitle="Bouldering Project"
                body="26,000 sq. ft. indoor bouldering gym with a yoga and fitness studio, training room and more."
              >
                <BPLogo className="spec__logo" />
              </Spec>
            </SwiperSlide>
            <SwiperSlide>
              <Spec
                subTitle="Level 9 Sports"
                body="A longtime fixture of the Salt Lake outdoor scene, L9 specializes in ski, snowboard, and bike gear."
              >
                <L9Logo className="spec__logo" />
              </Spec>
            </SwiperSlide>
            <SwiperSlide>
              <Spec
                subTitle="All Together Skatepark"
                body="5,000 Sq. Ft. indoor/outdoor space thoughtfully designed for skaters of all abilities to progress their skills."
              >
                <AtsLogo className="spec__logo" />
              </Spec>
            </SwiperSlide>
            <SwiperSlide>
              <Spec
                subTitle="evo Hotel"
                body="Designed for people who love the outdoors, evo Hotel is your launchpad for adventure in the Salt Lake area."
              >
                <EvoHotelLogo className="spec__logo w-auto" />
              </Spec>
            </SwiperSlide>
          </Swiper>
        </div>
        <div className="spec-container spec-container--desktop spec-container--the-vision">
          <Spec
            subTitle="Bouldering Project"
            body="26,000 sq. ft. indoor bouldering gym with a yoga and fitness studio, training room and more."
          >
            <BPLogo className="spec__logo" />
          </Spec>
          <Spec
            subTitle="Level 9 Sports"
            body="A longtime fixture of the Salt Lake outdoor scene, L9 specializes in ski, snowboard, and bike gear."
          >
            <L9Logo className="spec__logo" />
          </Spec>
          <Spec
            subTitle="All Together Skatepark"
            body="5,000 Sq. Ft. indoor/outdoor space thoughtfully designed for skaters of all abilities to progress their skills."
          >
            <AtsLogo className="spec__logo" />
          </Spec>
          <Spec
            subTitle="evo Hotel"
            body="Designed for people who love the outdoors, evo Hotel is your launchpad for adventure in the Salt Lake area."
          >
            <EvoHotelLogo className="spec__logo" />
          </Spec>
        </div>
      </FiftyFiftyHorizontal>

      <FiftyFiftyHorizontal
        className="spacing spacing--general"
        mediaPosition="left"
        contentColumns={5}
        contentOffset={2}
        mediaColumns={4}
        mediaOffset={1}
        imageData={useTheVisionPageImages({name: "the-vision-16"})}
      >
        <div className="the-vision__content-wrapper">
          <div className="the-vision__pre-text mb-4">About Lake Union Partners</div>
          <p>evo is working with Lake Union Partners to develop Campus Salt Lake. evo and Lake Union Partners share a passion for revitalizing historic buildings. This Seattle-based urban commercial real estate company focuses on mixed-use, historic preservation, and creative office projects. They create places with design integrity, intentionality, and sensitivity to local neighborhoods.</p>
          <div className="mt-5">
            <ArrowLink
              className="the-vision__link"
              isExternalLink={true}
              to="https://lakeunionpartners.com/"
              text="Learn More"
              theme="dark"
            />
          </div>
        </div>
      </FiftyFiftyHorizontal>

      <GetUpdates
        className="spacing spacing--general"
      />
      <DetailsFooter
        imageData={useTheVisionPageImages({name: "the-vision-13"})}
        imageDataHover={useTheVisionPageImages({name: "the-vision-14"})}
        linkData={{
          title: "Read Story",
          project_url: "/evo-hotel/",
        }}
        pre_title="Up Next"
        title="evo Hotel"
      />
    </Layout>
  )
}

export default TheVision
